import { AppContext } from "App";

import { IDataReaderClient } from "@multiweb/clients/dataReader";
import { INotificationClient, NotificationListener } from "@multiweb/clients/notifications";
import { IAttrValue, IconCode, INotificationGroupType, INotificationItem, INotificationType, IRowData, NotificationFunctionType } from "@multiweb/types/contracts";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { IOWClassDescription } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { Enum } from "OW.Remoting/Remoting/DataTypes/Enums";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { RequestContextWithoutTransactionContextId } from "OW.Remoting/Remoting/RequestContextWithoutTransactionContextId";
import { MEChangeStack, MEConditionGroup } from "UI/UI.Controls/DataSource/MultiEdit";
import { ATTRIBUTE_CREATED_DATE, ATTRIBUTE_FORM_CODE, ATTRIBUTE_FUNCTION as ATTRIBUTE_ACTION_TYPE, ATTRIBUTE_ID, ATTRIBUTE_OBJECT_ID, ATTRIBUTE_PENDING, ATTRIBUTE_TEXT, ATTRIBUTE_TITLE, ATTRIBUTE_TYPE_COLOR, ATTRIBUTE_TYPE_ICON, ATTRIBUTE_TYPE_NAME, ATTRIBUTES, CLASS_NAME, GROUP_ATTRIBUTES, GROUP_CLASS_NAME, GROUP_CONDITION, NOTIFICATION_CONDITION, ORDERING, PENDING_NOTIFICATION_CONDITION } from "./constants";

/**
 * @internal
 */
export class NotificationClient implements INotificationClient {
    private _notificationInterfaceMetadataPromise?: Promise<IOWClassDescription>;
    private _listeners: NotificationListener[] = [];
    private readonly _dataReaderClient: IDataReaderClient;
    private readonly _dataEventsManager: IDataEventsManager;

    constructor(dataReaderClient: IDataReaderClient, dataEventsManager: IDataEventsManager) {
        this._dataReaderClient = dataReaderClient;
        this._dataEventsManager = dataEventsManager;
    }

    public async getNotificationGroups(cancellationToken: CancellationToken = CancellationToken.none): Promise<readonly Readonly<INotificationGroupType>[]> {
        const groupRows = await this._dataReaderClient.getDataRowsWithParams(
            GROUP_CLASS_NAME,
            GROUP_ATTRIBUTES,
            GROUP_CONDITION,
            undefined,
            undefined,
            this._getRequestContext(cancellationToken));

        const groups: INotificationGroupType[] = groupRows.map(v => ({
            name: AttrValueReader.readRow(v, "Name"),
            formCode: AttrValueReader.readRow(v, "ViewAllForm.kod_formulare"),
            iconCode: AttrValueReader.readRow(v, "Icon"),
            titleLocKey: AttrValueReader.readRow(v, "LocKey"),
        }));
        return groups;
    }

    public existsUnreadNotification(groupName: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        return this._dataReaderClient.exists(
            CLASS_NAME,
            PENDING_NOTIFICATION_CONDITION,
            [ groupName ],
            undefined,
            this._getRequestContext(cancellationToken));
    }

    public async getNotificationItems(groupName: string, onlyUnread: boolean, startIndex: number, itemsCount: number, cancellationToken: CancellationToken = CancellationToken.none): Promise<readonly Readonly<INotificationItem>[]> {
        const result = await this._dataReaderClient.getDataRowsWithParams(
            CLASS_NAME,
            ATTRIBUTES,
            onlyUnread ? PENDING_NOTIFICATION_CONDITION : NOTIFICATION_CONDITION,
            {
                params: [ groupName ],
                orderBy: ORDERING,
                startIndex: startIndex,
                count: itemsCount,
            }, undefined, this._getRequestContext(cancellationToken));

        return result.map(row => createNotificationFromRow(row, groupName));
    }

    public async setNotificationAsRead(notificationId: number, markAsRead: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        const obj = await OWManager.getObject(notificationId, CLASS_NAME, undefined, undefined, undefined, this._getRequestContext(cancellationToken));
        await obj.setValue(ATTRIBUTE_PENDING, !markAsRead);
        await obj.applyUpdates();
    }

    private _onNotificationChange = () => {
        this._listeners.forEach(listener => listener());
    };

    public async subscribeNotificationChange(listener: NotificationListener): Promise<void> {
        if (this._listeners.length === 0) {
            if (!this._notificationInterfaceMetadataPromise)
                this._notificationInterfaceMetadataPromise = ModelDescriptionsManager.instance.getClassInfo(CLASS_NAME);

            const implementingClasses = (await this._notificationInterfaceMetadataPromise).childClasses;
            implementingClasses.forEach(className => this._dataEventsManager.subscribeClassChanges(className, this._onNotificationChange));
        }

        this._listeners.push(listener);
    }

    public async unsubscribeNotificationChange(listener: NotificationListener): Promise<void> {
        this._listeners = this._listeners.splice(this._listeners.indexOf(listener), 1);

        if (!this._notificationInterfaceMetadataPromise)
            return;

        if (this._listeners.length === 0) {
            const implementingClasses = (await this._notificationInterfaceMetadataPromise).childClasses;
            implementingClasses.forEach(className => this._dataEventsManager.unsubscribeClassChanges(className, listener));
        }
    }

    /**
     * Notifikace editačnímu kontextu nepodléhají
     * @param cancellationToken Umožňuje zrušit běžící dotaz
     */
    private _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContextWithoutTransactionContextId {
        return {
            roles: AppContext.instance.activeUserRolesIds,
            cancellationToken,
        };
    }

    /**
     * Označí všechny notifikace dané skupiny jako přečtené
     * @param notificationGroupId Identifikátor skupiny
     * @param cancellationToken Umožňuje zrušit stávající dotaz
     */
    public async setGroupNotificationsAsRead(notificationGroupId: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        const group = new MEConditionGroup(PENDING_NOTIFICATION_CONDITION, [notificationGroupId]);
        group.addChange(ATTRIBUTE_PENDING, false);

        const context = this._getRequestContext(cancellationToken);
        const stack = new MEChangeStack(CLASS_NAME, (_id) => null, context, undefined);
        await stack.add(group, context);

        await OWManager.applyMultiEditUpdates(stack, context);
    }
}

function createNotificationFromRow(rowData: IRowData, notificationGroupId: string): INotificationItem {
    return createNotification(
        rowData[ATTRIBUTE_ID],
        rowData[ATTRIBUTE_ACTION_TYPE],
        rowData[ATTRIBUTE_TITLE],
        rowData[ATTRIBUTE_TEXT],
        createNotificationType(
            rowData[ATTRIBUTE_TYPE_NAME],
            rowData[ATTRIBUTE_TYPE_ICON],
            rowData[ATTRIBUTE_TYPE_COLOR],
        ),
        rowData[ATTRIBUTE_FORM_CODE],
        rowData[ATTRIBUTE_OBJECT_ID],
        rowData[ATTRIBUTE_PENDING],
        rowData[ATTRIBUTE_CREATED_DATE],
        notificationGroupId,
    );
}

function createNotification(id: IAttrValue, functionType: IAttrValue, title: IAttrValue, text: IAttrValue, type: INotificationType,
    formId: IAttrValue, objId: IAttrValue, unread: IAttrValue, createdDate: IAttrValue, notificationGroupId: string): INotificationItem {
    const functionTypeEnum: Enum = AttrValueReader.read(functionType);
    return {
        id: AttrValueReader.read(id),
        functionType: functionTypeEnum ? functionTypeEnum.getName() as NotificationFunctionType : NotificationFunctionType.generic,
        title: AttrValueReader.read(title),
        text: AttrValueReader.read(text),
        type: type,
        formCode: AttrValueReader.read(formId),
        objId: AttrValueReader.read(objId),
        unread: AttrValueReader.read(unread),
        createdDate: AttrValueReader.read(createdDate),
        notificationGroupId: notificationGroupId,
    };
}

function createNotificationType(name: IAttrValue, icon: IAttrValue, color: IAttrValue): INotificationType {
    return {
        name: (name != null && name.toString().trim() != "") ? name.toString() : "",
        iconCode: (icon != null && icon.toString().trim() != "") ? icon.toString() as IconCode : IconCode.comBell,
        color: (color != null && color.toString().trim() != "") ? color.toString() : "gold",
    };
}
